import { type Decimal, dec, ZERO } from "./decimal";

/**
 * The cellar's tanks and what is in them (Scott's Jam, 2026-10-03: "we are going to need to start
 * building tanks and then assigning batches to tanks when they're brewed so we know that the tank is
 * full"). A brewery sets up its own tanks, each of a kind; a brew's time in a tank is a stay, from the
 * day it goes in to the day it comes out. A stay is a booking until the beer actually goes in, on brew
 * day or with a move, and holds its tank until it is emptied, however late that is.
 *
 * Nothing here is a quantity of stock. Beer in a tank is not counted as inventory yet (cellar work), so
 * stays are dates and nothing is written to the movement log.
 */

/** The kinds built in (Scott: "a few standard types of tanks"); a brewery can name its own as well. */
export const TANK_KINDS = ["Fermenter", "Brite tank", "Yeast brink"] as const;

/** What a tank's capacity is typed in: barrels by default, gallons or hectoliters for some breweries. */
export const TANK_UNITS = ["bbl", "gal", "hL"] as const;
export type TankUnit = (typeof TANK_UNITS)[number];

/** A kind as kept: a built-in one spelled as built in, whatever its case; any other as typed. */
export function tankKindOf(typed: string): string {
  const kind = typed.trim().replace(/\s+/g, " ");
  return TANK_KINDS.find((k) => k.toLowerCase() === kind.toLowerCase()) ?? kind;
}

/** Kinds in the order a list of tanks shows them: the built-in ones first, then the brewery's own. */
export function compareTankKinds(a: string, b: string): number {
  const rank = (k: string) => {
    const i = (TANK_KINDS as readonly string[]).indexOf(k);
    return i < 0 ? TANK_KINDS.length : i;
  };
  return rank(a) - rank(b) || a.localeCompare(b);
}

/** Tank names in the order the floor counts them: FV2 before FV10. */
export const compareTankNames = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

export type StayState = "booked" | "in" | "emptied";

export interface StayDays {
  inOn: string;
  dueOutOn: string | null;
  emptiedOn: string | null;
  /** The beer went in: on brew day, or with a move. Until then the stay is a booking. */
  filled: boolean;
}

export function stayState(s: StayDays): StayState {
  if (s.emptiedOn) return "emptied";
  return s.filled ? "in" : "booked";
}

/** In the tank past the day it was due out, and still not emptied. */
export const isOverdue = (s: StayDays, today: string): boolean =>
  stayState(s) === "in" && s.dueOutOn !== null && s.dueOutOn < today;

/**
 * The last day a stay holds its tank: the day it was emptied, else the day it is due out, else no end
 * (null) until someone says when. A beer still in past its day holds the tank through today.
 */
export function lastDayOf(s: StayDays, today: string): string | null {
  if (s.emptiedOn) return s.emptiedOn;
  if (s.dueOutOn === null) return null;
  return s.filled && s.dueOutOn < today ? today : s.dueOutOn;
}

/**
 * Whether two stays in one tank want it at the same time. The day one comes out and the other goes in
 * is not a clash: emptying a tank and filling it again the same day is the usual transfer day.
 */
export function staysOverlap(
  a: { inOn: string; lastOn: string | null },
  b: { inOn: string; lastOn: string | null },
): boolean {
  return (b.lastOn === null || a.inOn < b.lastOn) && (a.lastOn === null || b.inOn < a.lastOn);
}

export interface TankStaySpan {
  id: string;
  brewId: string;
  /** The recipe across its versions: two brews of one recipe in one tank are a double batch. */
  recipeKey: string;
  inOn: string;
  lastOn: string | null;
  /** What it puts in the tank, canonical liters. */
  liters: Decimal;
}

/**
 * How much of a tank's total volume a beer may fill without a warning: nine tenths (Scott, 2026-10-08,
 * through Tyler: "it should warn when 10% or less TV is left").
 */
export const TOTAL_VOLUME_FILL = "0.9";

/**
 * The most a tank takes without a warning. A tank is built bigger than its size, with room for the beer
 * to rise as it ferments (Scott, 2026-10-08: "a 15 bbl tank is alway built with extra space for
 * expansion during fermentation"), so a 16 bbl batch suits a 15 bbl fermenter: where the brewery gave
 * the tank's total volume, a beer may fill nine tenths of it. Without one, the tank takes its size.
 */
export function fillLimitLiters(t: {
  capacityLiters: string;
  totalLiters: string | null;
}): Decimal {
  return t.totalLiters === null
    ? dec(t.capacityLiters)
    : dec(t.totalLiters).times(dec(TOTAL_VOLUME_FILL));
}

export interface StayClashes {
  /** Other beers in the tank at the same time, beyond a transfer day. */
  doubleBooked: TankStaySpan[];
  /** The most the tank is asked to hold at once while this stay is in it, itself included. */
  peakLiters: Decimal;
  /** That is more than the tank takes (`fillLimitLiters`). */
  overCapacity: boolean;
}

/**
 * What is wrong with a stay among the others in its tank. A different recipe in it at the same time is
 * a double booking (Tyler, 2026-10-03, with the Tank schedule view). The same recipe is a double batch,
 * two brews filling one tank, and is only a problem when together they are more than the tank takes
 * (`limitLiters`, from `fillLimitLiters`). That counts the double batch alone: a different beer in the
 * tank is already the double booking.
 */
export function clashesOf(
  stay: TankStaySpan,
  others: readonly TankStaySpan[],
  limitLiters: Decimal,
): StayClashes {
  const sharing = others.filter(
    (o) => o.id !== stay.id && o.brewId !== stay.brewId && staysOverlap(stay, o),
  );
  const doubleBooked = sharing.filter((o) => o.recipeKey !== stay.recipeKey);
  // The tank holds the most on a day one of them goes in, so look at each of those days while this
  // one is in it. A stay coming out on a day holds nothing that day, as one going in does.
  const all = [stay, ...sharing.filter((o) => o.recipeKey === stay.recipeKey)];
  let peakLiters = ZERO;
  for (const t of all) {
    const day = t.inOn < stay.inOn ? stay.inOn : t.inOn;
    const held = all
      .filter((s) => s.inOn <= day && (s.lastOn === null || day < s.lastOn || s === stay))
      .reduce((sum, s) => sum.plus(s.liters), ZERO);
    if (held.gt(peakLiters)) peakLiters = held;
  }
  // To the liter: a 15 bbl batch typed in gallons is a 15 bbl tank's worth, not a drop over.
  return {
    doubleBooked,
    peakLiters,
    overCapacity: peakLiters.round(0).gt(limitLiters.round(0)),
  };
}

/**
 * Whether another beer in a tank fills it or only has it scheduled (Scott's Jam, 2026-10-08: "if I have
 * started brewing then it needs to say something like tank is full, and if I have something scheduled
 * it should say something like tank is scheduled but you should still allow me to start the brew"). It
 * fills the tank once it is in it, or once its brew day has started, when this is the tank it goes into
 * that day; a booking for later, or for a brew not started, is scheduled. Both are warnings only.
 */
export function fillsTank(
  stay: { state: StayState; inOn: string },
  brew: { status: "planned" | "draft" | "brewed"; brewedOn: string },
): boolean {
  return stay.state !== "booked" || (brew.status !== "planned" && stay.inOn <= brew.brewedOn);
}

/**
 * A recipe's times, in days (Scott's Jam, 2026-10-08: "instead of time in fermenter, we could write
 * fermentation time, dry hop time, and bright tank time", and a fourth, "logger slash crash time";
 * Tyler named them Fermentation Time, Dry Hop Time, Lager/Crash Time and Brite Tank Time). The first
 * three are spent in the fermenter, one after another; the last in a brite tank. They are the
 * recipe's, and every recipe has them (Scott's Jam, 2026-10-08: "Usual days in tank is something that
 * is going to need to be built into recipes not into the tank itself"; Tyler chose recipes only).
 */
export interface RecipeTimes {
  fermentation: number;
  dryHop: number;
  lagerCrash: number;
  brite: number;
}

/** How long a beer usually spends in each tank: its recipe's times, the fermenter's added up. */
export interface TankDays extends RecipeTimes {
  fermenter: number;
}

export const tankDaysFrom = (t: RecipeTimes): TankDays => ({
  fermentation: t.fermentation,
  dryHop: t.dryHop,
  lagerCrash: t.lagerCrash,
  brite: t.brite,
  fermenter: t.fermentation + t.dryHop + t.lagerCrash,
});

/**
 * The times a new recipe starts with until the brewer types its own: two weeks fermenting, no dry hop
 * or lager/crash, and three days in a brite tank. An import keeps the fermentation its file gives.
 */
export const NEW_RECIPE_TANK_DAYS: TankDays = tankDaysFrom({
  fermentation: 14,
  dryHop: 0,
  lagerCrash: 0,
  brite: 3,
});

/**
 * The days a brew starts its dry hop and its lager/crash in the fermenter, counted from its brew day,
 * which is when fermentation starts; none for a time the recipe does not have.
 */
export function fermenterSteps(
  brewedOn: string,
  d: RecipeTimes,
): { dryHopOn: string | null; lagerCrashOn: string | null } {
  return {
    dryHopOn: d.dryHop > 0 ? dayPlus(brewedOn, d.fermentation) : null,
    lagerCrashOn: d.lagerCrash > 0 ? dayPlus(brewedOn, d.fermentation + d.dryHop) : null,
  };
}

/**
 * How many squares across the Brewery layout's floor can be (Tyler, 2026-10-05: "could the layout
 * builder have a zoom function to increase brewery size?"): fewer draws each tank bigger, more makes
 * room for more tanks. The floor is two thirds as deep as it is wide, and 24 squares across until the
 * brewery sets its own.
 */
export const LAYOUT_WIDTHS = [12, 18, 24, 30, 36, 48, 60, 72] as const;
export const USUAL_LAYOUT_WIDTH = 24;

/** How many squares deep a floor that many squares across is. */
export const layoutDepth = (width: number): number => Math.round((width * 2) / 3);

/** How long a stay in a tank of a kind usually lasts; none for a kind with no usual length. */
export function usualDaysIn(kind: string, days: TankDays): number | null {
  if (kind === "Fermenter") return days.fermenter;
  if (kind === "Brite tank") return days.brite;
  return null;
}

/** Days from one ISO date to another, by the calendar. */
export function daysApart(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** An ISO date moved by some days. */
export function dayPlus(on: string, days: number): string {
  const d = new Date(`${on}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
